/**
 * ProtectedRoute Component
 * คอมโพเนนต์ป้องกันเส้นทาง
 *
 * Guards routes requiring authentication and/or specific permissions.
 * ป้องกันเส้นทางที่ต้องมีการยืนยันตัวตนและ/หรือสิทธิ์เฉพาะ
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/features/auth/useAuth'
import { AuthLoadingSpinner } from './AuthLoadingSpinner'
import type { Permission } from '@/shared/types/permissions'

interface ProtectedRouteProps {
  requiredPermissions?: Permission[]
  requireAll?: boolean // true = all permissions required, false = any
}

export function ProtectedRoute({ requiredPermissions, requireAll = true }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasAnyPermission, hasAllPermissions } = useAuth()
  const location = useLocation()

  // Show loading while checking auth status
  if (isLoading) {
    return <AuthLoadingSpinner />
  }

  // Not authenticated — redirect to login with return URL
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check permissions if specified
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions)

    if (!hasAccess) {
      return <Navigate to="/unauthorized" replace />
    }
  }

  return <Outlet />
}
